import { gql, useLazyQuery } from '@apollo/client';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { Category } from '@modules/common/types';
import { mapEdgeCategoryToCategory } from '@modules/common/utils';
import {
  GetSearchSuggestionsInput,
  Query,
  Reason,
  SuggestionItem,
} from 'generated/graphql';
import { useMemo, useState } from 'react';
import { useDebounce, useUpdateEffect } from 'react-use';
import { useGetUser } from 'v2/core/services/user';

const GET_SEARCH_SUGGESTIONS_QUERY = gql`
  query getSearchSuggestions(
    $tenantID: ID!
    $query: String!
    $include: [SuggestionItem!]!
    $maxPerType: Int
  ) {
    getSearchSuggestions(
      request: {
        tenantID: $tenantID
        query: $query
        include: $include
        maxPerType: $maxPerType
      }
    ) {
      reasons {
        id
        displayName
        metadata {
          category
        }
        type
      }
      entities {
        id
        displayName
        type
      }
    }
  }
`;

interface SearchSuggestionsInput {
  searchTerm: string;
  include?: SuggestionItem[];
  maxPerType?: number;
}
export function useFetchSearchSuggestions(
  {
    searchTerm,
    include = [SuggestionItem.Entity, SuggestionItem.Reason],
    maxPerType = 5,
  }: SearchSuggestionsInput,
  shouldSkipNetworkCall?: boolean,
) {
  const {
    tenant: { id: tenantID },
  } = useGetUser();

  /** Kept if for future reference, Issue: https://github.com/apollographql/apollo-client/issues/4150 */

  // const abortSignalRef = useRef(new AbortController());
  const { showErrorNotification } = useNotificationContext();
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  useDebounce(() => setDebouncedSearchTerm(searchTerm), 300, [searchTerm]);

  const [getSearchSuggestions, resp] = useLazyQuery<
    Query,
    GetSearchSuggestionsInput
  >(GET_SEARCH_SUGGESTIONS_QUERY, {
    variables: {
      tenantID,
      query: debouncedSearchTerm,
      include,
      maxPerType,
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: false,
  });

  // TODO(aryanshridhar): Remove the explicit type for category metadata
  // once BE enums support Complaint instead of Issues
  const mappedReasons = useMemo(() => {
    return resp.data?.getSearchSuggestions?.reasons?.map((reason) => {
      const mappedReasonLabel: Omit<Reason, 'metadata'> & {
        metadata?: { category?: Category };
      } = {
        ...reason,
        metadata: {
          category: mapEdgeCategoryToCategory(reason.metadata?.category),
        },
      };

      return mappedReasonLabel;
    });
  }, [resp.data?.getSearchSuggestions?.reasons]);

  useUpdateEffect(() => {
    if (!debouncedSearchTerm || shouldSkipNetworkCall) {
      return;
    }

    // Required to abort ongoing network call
    // abortSignalRef.current = new AbortController();
    getSearchSuggestions({
      variables: {
        tenantID,
        query: debouncedSearchTerm,
        include,
        maxPerType,
      },
      // context: {
      //   fetchOptions: {
      //     get signal() {
      //       return abortSignalRef.current.signal;
      //     },
      //   },
      // },
    }).catch(() => {
      showErrorNotification({
        contentComponent: 'Failed to query suggestions.',
        props: {},
      });
    });

    // return () => abortSignalRef.current.abort();
  }, [debouncedSearchTerm]);

  // TODO(aryanshridhar): Remove this mapped response once BE enums support
  // Complaint instead of Issues for category metadata
  const mappedResp = {
    ...resp,
    data: {
      ...resp.data,
      getSearchSuggestions: {
        ...resp.data?.getSearchSuggestions,
        entities: resp.data?.getSearchSuggestions?.entities,
        reasons: mappedReasons,
      },
    },
  };

  return {
    ...mappedResp,
    debouncedSearchTerm,
    loading: resp.data ? false : resp.loading,
  };
}
